import { showToast } from '@/features/toasts/toastsSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { isCreditsFeatureUnavailable } from '@/features/campaigns/v2/credits/utils/isCreditsFeatureUnavailable';
import { useCreateStepper } from '@/lib/Steppers';
import { CreateStepArgs } from '@/lib/Steppers/CreateStepper/types';
import { ExpressionCondition } from '@/models/audiences';
import { ToastType } from '@/models/toast';
import { useAppDispatch } from '@/reducers';
import { usePostEstimateMutation } from '@/services/endpoints/audiences';
import { useGetGeoDatasourceStatesQuery } from '@/services/endpoints/geo';
import { useGetCreditBalancesQuery } from '@/services/endpoints/credits';
import { uniq } from 'lodash';
import { createContext, FC, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Channel } from '../ChannelStepContainer';
import { ChannelConfigs } from '../../components/wizard/ChannelFlowsStep';
import { ChannelSendConfig } from '../../components/wizard/ChannelSendFlowConfig';
import { Counts, SentResult } from '../../components/wizard/PreviewSendStep';
import {
  COMUNI_NEW_COMMUNICATION_STEPS,
  COMUNI_NEW_COMMUNICATION_STEPS_ITEM,
  ComuniNewCommunicationStepsItem,
  NO_TOPIC,
  TopicChoice,
} from '../../comuni-helper';
import { buildExpression, estimateExpressions, GeoZone, isCompleteFilter, withZone } from '../../expression';
import { GeoAvailability, geoAvailability } from '../geo/geoAvailability';
import { useChannelAvailability } from '../../useChannelAvailability';
import { ComuniList, useComuniLists } from '../../useComuniLists';
import { isCitizenFormList } from '../../listKind';
import { useDraftCommunication } from '../../useDraftCommunication';
import { createInitialFilterGroups, EMPTY_CONDITION, FilterGroup } from './types';

const topicBracketLabel = (topicId: TopicChoice, translate: (key: string) => string) => (topicId === NO_TOPIC ? '' : `[${translate(`topics.${topicId}`)}]`);

const adjustCommNameForTopicChange = (name: string, prevTopic: TopicChoice, nextTopic: TopicChoice, translate: (key: string) => string): string => {
  const oldPrefix = topicBracketLabel(prevTopic, translate);
  const newPrefix = topicBracketLabel(nextTopic, translate);
  const trimmed = name.trim();

  if (!trimmed || (oldPrefix && trimmed === oldPrefix)) {
    return newPrefix;
  }

  if (oldPrefix && name.startsWith(oldPrefix)) {
    const suffix = name.slice(oldPrefix.length).replace(/^\s+/, '');
    if (!newPrefix) return suffix;
    return suffix ? `${newPrefix} ${suffix}` : newPrefix;
  }

  return name;
};

export type NewCommunicationValuesApi = {
  // lists
  baseLists: ComuniList[];
  isListsLoading: boolean;
  selectedIds: string[];
  selectedLists: ComuniList[];
  selectedDatasourceIds: string[];
  // filters
  filterGroups: FilterGroup[];
  groupedConditions: ExpressionCondition[][];
  activeFilterCount: number;
  suggestedName: string;
  // geographic zone
  zone: GeoZone | null;
  geo: GeoAvailability;
  // estimates
  counts: Counts | null;
  stale: boolean;
  isEstimating: boolean;
  isAdvancing: boolean;
  // later steps (reserved)
  topic: TopicChoice;
  commName: string;
  channels: Channel[];
  channelConfigs: ChannelConfigs;
  sent: SentResult | null;
  draftAudienceId: string | null;
  showCosts: boolean;
  availability: ReturnType<typeof useChannelAvailability>['availability'];
  // stepper
  steps: CreateStepArgs<ComuniNewCommunicationStepsItem>[];
  currentStepId: ComuniNewCommunicationStepsItem;
  currentStepIndex: number;
  communicationName: string;
};

export type NewCommunicationActionsApi = {
  toggleList: (id: string) => void;
  addRule: () => void;
  updateRow: (groupKey: number, rowKey: number, condition: ExpressionCondition) => void;
  removeRow: (groupKey: number, rowKey: number) => void;
  splitGroupAt: (groupKey: number, rowKey: number) => void;
  mergeGroupIntoPrevious: (groupKey: number) => void;
  setZone: (zone: GeoZone | null) => void;
  /** Citizens of the selected lists inside the given zone alone (no other filter), for the live count of the map. */
  estimateZone: (zone: GeoZone) => Promise<number>;
  recalc: () => Promise<void>;
  handleSaveAsList: (name: string) => Promise<boolean>;
  handleRecipientsNext: () => Promise<void>;
  resetWizard: () => void;
  setTopic: (topic: TopicChoice) => void;
  setCommName: (name: string) => void;
  toggleChannel: (channel: Channel) => void;
  handleChannelConfigured: (channel: Channel, config: ChannelSendConfig) => void;
  handleChannelReset: (channel: Channel) => void;
  setSent: (sent: SentResult | null) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  completeCurrentStep: () => void;
  handleTopicNext: () => Promise<void>;
  handleChannelNext: () => Promise<void>;
};

export const NewCommunicationValuesContext = createContext<NewCommunicationValuesApi | null>(null);
export const NewCommunicationActionsContext = createContext<NewCommunicationActionsApi | null>(null);

export const NewCommunicationProvider: FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation('comuni');
  const { id: workspaceId } = useWorkspace();
  const dispatch = useAppDispatch();
  const { lists, isLoading: isListsLoading } = useComuniLists();

  const {
    steps,
    currentStepId,
    currentStepIndex,
    goToNextStep,
    goToPreviousStep,
    completeCurrentStep,
    reset: resetStepper,
  } = useCreateStepper(COMUNI_NEW_COMMUNICATION_STEPS);

  const keyRef = useRef(0);
  const nextKey = useCallback(() => ++keyRef.current, []);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filterGroups, setFilterGroups] = useState<FilterGroup[]>(() => {
    keyRef.current = 0;
    return createInitialFilterGroups(nextKey);
  });
  const [zone, setZoneState] = useState<GeoZone | null>(null);
  const [counts, setCounts] = useState<Counts | null>(null);
  const [stale, setStale] = useState(false);
  const [isEstimating, setIsEstimating] = useState(false);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [topic, setTopic] = useState<TopicChoice>(NO_TOPIC);
  const [commName, setCommName] = useState('');
  const [channels, setChannels] = useState<Channel[]>(['whatsapp']);
  const [channelConfigs, setChannelConfigs] = useState<ChannelConfigs>({});
  const [sent, setSent] = useState<SentResult | null>(null);

  const draft = useDraftCommunication();
  const draftAudienceId = draft.currentId();
  const [postEstimate] = usePostEstimateMutation();
  const { availability } = useChannelAvailability();
  const { data: balances, isError: balancesError, error: balancesErrorObj } = useGetCreditBalancesQuery({ workspaceId }, { skip: !workspaceId });
  const showCosts = !isCreditsFeatureUnavailable(balancesErrorObj) && !balancesError && Array.isArray(balances) && balances.length > 0;

  const baseLists = useMemo(() => lists.filter((list) => list.isUpload || isCitizenFormList(list.audience, list.datasources)), [lists]);
  const selectedLists = useMemo(() => baseLists.filter((list) => selectedIds.includes(list.id)), [baseLists, selectedIds]);
  const selectedDatasourceIds = useMemo(() => uniq(selectedLists.flatMap((list) => list.audience.datasourceIds ?? [])), [selectedLists]);
  const groupedConditions = useMemo(() => filterGroups.map((group) => group.rows.map((row) => row.condition)), [filterGroups]);
  const activeFilterCount = useMemo(() => groupedConditions.flat().filter(isCompleteFilter).length, [groupedConditions]);
  /** Rule rows plus the zone: what estimate, save-as-list and the draft audience actually carry. */
  const estimateGroups = useMemo(() => withZone(groupedConditions, zone), [groupedConditions, zone]);
  const geoStates = useGetGeoDatasourceStatesQuery(
    { workspaceId, datasourceIds: selectedDatasourceIds },
    { skip: !workspaceId || !selectedDatasourceIds.length, pollingInterval: 60_000 }
  );
  // no selection, or an API without geolocation support (feature off, endpoint not deployed): nothing qualifies;
  // currentData, not data: a changed selection is loading until its own states arrive, never the previous lists'
  const geo = useMemo(
    () => geoAvailability(!selectedDatasourceIds.length || geoStates.isError ? [] : geoStates.currentData),
    [geoStates.currentData, geoStates.isError, selectedDatasourceIds.length]
  );
  const suggestedName = useMemo(() => selectedLists.map((list) => list.name).join(' + '), [selectedLists]);
  /**
   * One list, no filters: the composer would recreate that list's audience identically, so reuse it.
   * The copy would have no evaluated snapshot, and activating a campaign only replays the snapshot,
   * so the communication would go out to nobody. Save-as-list still creates a real audience.
   */
  const reusableAudienceId = useMemo(
    () => (selectedLists.length === 1 && activeFilterCount === 0 && !zone ? selectedLists[0].id : undefined),
    [selectedLists, activeFilterCount, zone]
  );
  const communicationName = commName;

  const selectTopic = useCallback(
    (next: TopicChoice) => {
      setCommName((name) => adjustCommNameForTopicChange(name, topic, next, t));
      if (next !== topic) setChannelConfigs({});
      setTopic(next);
    },
    [topic, t]
  );

  useEffect(() => {
    if (currentStepId !== COMUNI_NEW_COMMUNICATION_STEPS_ITEM.CHANNELS) return;
    setChannels((current) => current.filter((c) => availability[c] !== false));
  }, [availability.whatsapp, availability.sms, availability.email, currentStepId]);

  useEffect(() => {
    setChannelConfigs((prev) => {
      const next: ChannelConfigs = {};
      channels.forEach((channel) => {
        if (prev[channel]) next[channel] = prev[channel];
      });
      return next;
    });
  }, [channels]);

  useEffect(() => {
    setChannelConfigs({});
  }, [draftAudienceId]);

  // the zone is defined over the selected lists (their streets, their citizens on the map): a
  // different selection makes it meaningless, and its readiness is not re-checked downstream
  const toggleList = useCallback((id: string) => {
    setSelectedIds((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));
    setZoneState(null);
    setStale(true);
  }, []);

  const addRule = useCallback(() => {
    setFilterGroups((current) => [...current, { key: nextKey(), rows: [{ key: nextKey(), condition: EMPTY_CONDITION }] }]);
  }, [nextKey]);

  const updateRow = useCallback((groupKey: number, rowKey: number, condition: ExpressionCondition) => {
    setFilterGroups((current) =>
      current.map((group) => (group.key === groupKey ? { ...group, rows: group.rows.map((row) => (row.key === rowKey ? { ...row, condition } : row)) } : group))
    );
    setStale(true);
  }, []);

  const removeRow = useCallback((groupKey: number, rowKey: number) => {
    setFilterGroups((current) =>
      current
        .map((group) => (group.key === groupKey ? { ...group, rows: group.rows.filter((row) => row.key !== rowKey) } : group))
        .filter((group) => group.rows.length > 0)
    );
    setStale(true);
  }, []);

  const splitGroupAt = useCallback(
    (groupKey: number, rowKey: number) => {
      setFilterGroups((current) =>
        current.flatMap((group) => {
          if (group.key !== groupKey) return [group];
          const index = group.rows.findIndex((row) => row.key === rowKey);
          if (index <= 0) return [group];
          return [
            { ...group, rows: group.rows.slice(0, index) },
            { key: nextKey(), rows: group.rows.slice(index) },
          ];
        })
      );
      setStale(true);
    },
    [nextKey]
  );

  const mergeGroupIntoPrevious = useCallback((groupKey: number) => {
    setFilterGroups((current) => {
      const index = current.findIndex((group) => group.key === groupKey);
      if (index <= 0) return current;
      const merged = [...current];
      merged[index - 1] = { ...merged[index - 1], rows: [...merged[index - 1].rows, ...merged[index].rows] };
      merged.splice(index, 1);
      return merged;
    });
    setStale(true);
  }, []);

  const setZone = useCallback((next: GeoZone | null) => {
    setZoneState(next);
    setStale(true);
  }, []);

  const estimateZone = useCallback(
    async (candidate: GeoZone): Promise<number> => {
      const audienceId = await draft.ensure(selectedDatasourceIds, reusableAudienceId);
      const estimate = await postEstimate({ workspaceId, audienceId, audience: { expression: buildExpression(withZone([], candidate)) } }).unwrap();
      return estimate.size;
    },
    [draft, selectedDatasourceIds, reusableAudienceId, postEstimate, workspaceId]
  );

  const runEstimates = useCallback(async (): Promise<Counts | null> => {
    const audienceId = await draft.ensure(selectedDatasourceIds, reusableAudienceId);
    const expressions = estimateExpressions(estimateGroups);
    const [total, withPhone, withEmail, reachable] = await Promise.all([
      postEstimate({ workspaceId, audienceId, audience: { expression: expressions.total } }).unwrap(),
      postEstimate({ workspaceId, audienceId, audience: { expression: expressions.withPhone } }).unwrap(),
      postEstimate({ workspaceId, audienceId, audience: { expression: expressions.withEmail } }).unwrap(),
      postEstimate({ workspaceId, audienceId, audience: { expression: expressions.reachable } }).unwrap(),
    ]);
    const next: Counts = {
      total: total.size,
      withPhone: withPhone.size,
      withEmail: withEmail.size,
      unreachable: Math.max(0, total.size - reachable.size),
    };
    setCounts(next);
    setStale(false);
    return next;
  }, [draft, selectedDatasourceIds, reusableAudienceId, estimateGroups, postEstimate, workspaceId]);

  const recalc = useCallback(async () => {
    if (!selectedIds.length) return;
    setIsEstimating(true);
    try {
      await runEstimates();
    } catch {
      dispatch(showToast({ type: ToastType.ERROR, title: t('composer.estimateError') }));
    } finally {
      setIsEstimating(false);
    }
  }, [selectedIds.length, runEstimates, dispatch, t]);

  const selectionKey = selectedDatasourceIds.join('|');
  useEffect(() => {
    if (!selectedDatasourceIds.length || currentStepId !== COMUNI_NEW_COMMUNICATION_STEPS_ITEM.RECIPIENTS) {
      return undefined;
    }
    const timer = setTimeout(() => {
      void recalc();
    }, 700);
    return () => clearTimeout(timer);
  }, [selectionKey, currentStepId]);

  const toggleChannel = useCallback((channel: Channel) => {
    setChannels((current) => (current.includes(channel) ? current.filter((c) => c !== channel) : [...current, channel]));
  }, []);

  const handleChannelConfigured = useCallback((channel: Channel, config: ChannelSendConfig) => {
    setChannelConfigs((prev) => ({ ...prev, [channel]: config }));
  }, []);

  const handleChannelReset = useCallback((channel: Channel) => {
    setChannelConfigs((prev) => {
      if (!prev[channel]) return prev;
      const next = { ...prev };
      delete next[channel];
      return next;
    });
  }, []);

  const handleSaveAsList = useCallback(
    async (name: string) => {
      try {
        await draft.ensure(selectedDatasourceIds);
        await draft.persist(buildExpression(estimateGroups), (name || suggestedName).trim());
        draft.retain();
        draft.release();
        dispatch(showToast({ type: ToastType.SUCCESS, title: t('composer.saved') }));
        return true;
      } catch {
        dispatch(showToast({ type: ToastType.ERROR, title: t('composer.saveError') }));
        return false;
      }
    },
    [draft, selectedDatasourceIds, estimateGroups, suggestedName, dispatch, t]
  );

  const handleRecipientsNext = useCallback(async () => {
    setIsAdvancing(true);
    try {
      await draft.ensure(selectedDatasourceIds, reusableAudienceId);
      await draft.persist(buildExpression(estimateGroups));
      if (!counts || stale) await runEstimates();
      completeCurrentStep();
      goToNextStep();
    } catch {
      dispatch(showToast({ type: ToastType.ERROR, title: t('composer.saveError') }));
    } finally {
      setIsAdvancing(false);
    }
  }, [draft, selectedDatasourceIds, reusableAudienceId, estimateGroups, counts, stale, runEstimates, completeCurrentStep, goToNextStep, dispatch, t]);

  const handleTopicNext = useCallback(async () => {
    if (!commName.trim()) return;
    completeCurrentStep();
    goToNextStep();
  }, [commName, completeCurrentStep, goToNextStep]);

  const handleChannelNext = useCallback(async () => {
    completeCurrentStep();
    goToNextStep();
  }, [completeCurrentStep, goToNextStep]);

  // After campaigns are created, detach the draft so unmount cleanup cannot delete the
  // audience those campaigns still reference (and so a later ensure cannot overwrite it).
  const setSentResult = useCallback(
    (result: SentResult | null) => {
      if (result != null) {
        draft.release();
      }
      setSent(result);
    },
    [draft]
  );

  const resetWizard = useCallback(() => {
    draft.release();
    setSelectedIds([]);
    keyRef.current = 0;
    setFilterGroups(createInitialFilterGroups(nextKey));
    setZoneState(null);
    setCounts(null);
    setStale(false);
    setTopic(NO_TOPIC);
    setCommName('');
    setChannels(['whatsapp']);
    setChannelConfigs({});
    setSent(null);
    resetStepper();
  }, [draft, nextKey, resetStepper]);

  const values = useMemo(
    () =>
      ({
        baseLists,
        isListsLoading,
        selectedIds,
        selectedLists,
        selectedDatasourceIds,
        filterGroups,
        groupedConditions,
        activeFilterCount,
        suggestedName,
        zone,
        geo,
        counts,
        stale,
        isEstimating,
        isAdvancing,
        topic,
        commName,
        channels,
        channelConfigs,
        sent,
        draftAudienceId,
        showCosts,
        availability,
        steps,
        currentStepId,
        currentStepIndex,
        communicationName,
      }) satisfies NewCommunicationValuesApi,
    [
      baseLists,
      isListsLoading,
      selectedIds,
      selectedLists,
      selectedDatasourceIds,
      filterGroups,
      groupedConditions,
      activeFilterCount,
      suggestedName,
      zone,
      geo,
      counts,
      stale,
      isEstimating,
      isAdvancing,
      topic,
      commName,
      channels,
      channelConfigs,
      sent,
      draftAudienceId,
      showCosts,
      availability,
      steps,
      currentStepId,
      currentStepIndex,
      communicationName,
    ]
  );

  const actions = useMemo(
    () =>
      ({
        toggleList,
        addRule,
        updateRow,
        removeRow,
        splitGroupAt,
        mergeGroupIntoPrevious,
        setZone,
        estimateZone,
        recalc,
        handleSaveAsList,
        handleRecipientsNext,
        resetWizard,
        setTopic: selectTopic,
        setCommName,
        toggleChannel,
        handleChannelConfigured,
        handleChannelReset,
        setSent: setSentResult,
        goToNextStep,
        goToPreviousStep,
        completeCurrentStep,
        handleTopicNext,
        handleChannelNext,
      }) satisfies NewCommunicationActionsApi,
    [
      toggleList,
      addRule,
      updateRow,
      removeRow,
      splitGroupAt,
      mergeGroupIntoPrevious,
      setZone,
      estimateZone,
      recalc,
      handleSaveAsList,
      handleRecipientsNext,
      resetWizard,
      selectTopic,
      toggleChannel,
      handleChannelConfigured,
      handleChannelReset,
      setSentResult,
      goToNextStep,
      goToPreviousStep,
      completeCurrentStep,
      handleTopicNext,
      handleChannelNext,
    ]
  );

  return (
    <NewCommunicationValuesContext.Provider value={values}>
      <NewCommunicationActionsContext.Provider value={actions}>{children}</NewCommunicationActionsContext.Provider>
    </NewCommunicationValuesContext.Provider>
  );
};
